import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { AlertCircle, FileDown, Share2 } from "lucide-react";
import { toast } from "sonner";
import { generateFeasibilityPDF, generatePDFBlob } from "@/utils/pdfExport";
import { calculateOurOfferMetrics, calculateDualJVComparison } from "@/utils/calculations";

export const PLSheet = ({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, competitorData }) => {

  const plData = useMemo(() => {
    // Use shared calculation function to ensure consistency with Comparison tab
    const baseMetrics = calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
    if (!baseMetrics) {
      return null;
    }

    // ============ FEASIBILITY SUMMARY CALCULATIONS (PLSheet specific) ============
    
    // Use canonical initial investment from shared calculations (single source of truth)
    const { initialInvestment, tdrCost, paidFSIAncillaryFull, ulcCharges, 
            bgBaseAmount, bgRatePA, durationYears, sanctionCostsExTDRPaid,
            shiftingInInitial, bettermentOnVacation } = baseMetrics;
    
    // BG cost for display
    const bankGuaranteeCost = baseMetrics.bankGuaranteeCost;
    
    // Note: Interest on Self Investment is NOT calculated separately here
    // because it's already included in B6. Bank Interest (Interest Part B)
    // which covers the cost of capital on project investments
    
    // Net Profit = Gross Profit (Bank Interest already deducted in Total Expenses)
    const netProfit = baseMetrics.grossProfit;
    
    // Number of flats for sale (assuming average flat size)
    const avgFlatSizeSqft = 800; // Default average flat size
    const numFlatsForSale = Math.floor(baseMetrics.finalSaleAreaSqft / avgFlatSizeSqft);
    
    // Proposed Building Height
    const proposedBuildingHeight = parseFloat(formData.buildingHeight) || 0;

    return {
      // From shared calculation (ensuring consistency with Comparison tab)
      ...baseMetrics,
      // Feasibility Summary data (PLSheet specific)
      initialInvestment, 
      netProfit,
      numFlatsForSale, 
      proposedBuildingHeight,
      tdrCost, 
      paidFSIAncillaryFull, 
      ulcCharges,
      bankGuaranteeCost,
      bgBaseAmount,
      bgRatePA,
      durationYears,
      shiftingInInitial,
      bettermentOnVacation,
      // Dual JV comparison (for Greenfield projects)
      dualJV: calculateDualJVComparison(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts),
    };
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCrores = (value) => {
    const crores = value / 10000000;
    return `₹${crores.toFixed(2)} Cr`;
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    }).format(value);
  };

  // PDF Export Handler
  const handleExportPDF = () => {
    try {
      const fileName = generateFeasibilityPDF(
        formData,
        fsiBreakdown,
        fsiCosts,
        daChargesCosts,
        societyCosts,
        plData,
        competitorData
      );
      toast.success(`PDF exported: ${fileName}`);
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error('Failed to export PDF. Please try again.');
    }
  };

  // WhatsApp Share Handler
  const handleShareWhatsApp = async () => {
    try {
      // Format currency for message
      const formatCrores = (value) => {
        if (!value || isNaN(value)) return '₹0 Cr';
        return `₹${(value / 10000000).toFixed(2)} Cr`;
      };

      // Create summary message
      const projectName = formData.projectName || 'Untitled Project';
      const societyName = formData.societyName || 'Society';
      const location = formData.location || '';
      const totalRevenue = formatCrores(plData?.totalRevenue);
      const totalExpenses = formatCrores(plData?.totalExpenses);
      const grossProfit = formatCrores(plData?.grossProfit);
      const profitMargin = plData?.profitMarginPercent?.toFixed(1) || '0';
      const roi = plData?.returnOnInvestment?.toFixed(1) || '0';
      const status = plData?.grossProfit >= 0 ? '✅ VIABLE' : '❌ NOT VIABLE';

      const message = `*FEASIBILITY REPORT*
━━━━━━━━━━━━━━━
📋 *${projectName}*
🏢 ${societyName}
📍 ${location}

*Financial Summary:*
💰 Revenue: ${totalRevenue}
💸 Expenses: ${totalExpenses}
📊 Profit: ${grossProfit}
📈 Margin: ${profitMargin}%
🎯 ROI: ${roi}%

*Status: ${status}*
━━━━━━━━━━━━━━━
_Generated by Feasibility Calculator_`;

      // Try Web Share API first (works on mobile with file sharing)
      if (navigator.share && navigator.canShare) {
        try {
          const pdfBlob = generatePDFBlob(
            formData,
            fsiBreakdown,
            fsiCosts,
            daChargesCosts,
            societyCosts,
            plData,
            competitorData
          );
          
          const fileName = `Feasibility_Report_${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
          const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Feasibility Report - ${projectName}`,
              text: message,
              files: [file]
            });
            toast.success('Report shared successfully!');
            return;
          }
        } catch (shareError) {
          console.log('File sharing not supported, falling back to text share');
        }
      }

      // Fallback: Open WhatsApp with text message
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      toast.success('Opening WhatsApp...');
      
    } catch (error) {
      console.error('WhatsApp Share Error:', error);
      toast.error('Failed to share. Please try again.');
    }
  };

  if (!fsiBreakdown) {
    return (
      <Card className="rounded-sm shadow-sm bg-secondary/50">
        <CardContent className="py-12 text-center text-muted-foreground">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Complete Previous Tabs First</p>
          <p className="text-sm mt-2">Please fill in Property, FSI, and other tabs to generate the P&L Sheet</p>
        </CardContent>
      </Card>
    );
  }

  if (!plData) {
    return (
      <Card className="rounded-sm shadow-sm bg-secondary/50">
        <CardContent className="py-12 text-center text-muted-foreground">
          <p>Calculating...</p>
        </CardContent>
      </Card>
    );
  }

  const isProfitable = plData.grossProfit >= 0;

  return (
    <div className="space-y-6">
      {/* ============ FEASIBILITY SUMMARY SHEET ============ */}
      <Card className="rounded-sm shadow-lg border-2 border-primary/30" data-testid="feasibility-summary-card">
        <CardHeader className="bg-primary/10 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading tracking-tight text-2xl">FEASIBILITY SUMMARY</CardTitle>
              <CardDescription>{formData.projectName || 'Untitled Project'} - {formData.projectMode === "greenfield" ? (formData.projectName || 'JV Project') : (formData.societyName || 'Society')}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                type="button"
                variant="default" 
                size="sm" 
                onClick={handleExportPDF}
                className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="export-pdf-btn"
              >
                <FileDown className="h-4 w-4" />
                Download PDF
              </Button>
              <Button 
                type="button"
                variant="default" 
                size="sm" 
                onClick={handleShareWhatsApp}
                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                data-testid="share-whatsapp-btn"
              >
                <Share2 className="h-4 w-4" />
                WhatsApp
              </Button>
            </div>
          </div>
          {/* Executive Summary Strip */}
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="executive-summary-strip">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm text-center">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">Initial Investment</p>
              <p className="font-mono font-bold text-sm text-amber-700 truncate" data-testid="exec-initial-investment">{formatCrores(plData.initialInvestment)}</p>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm text-center">
              <p className="text-[10px] uppercase tracking-wider text-blue-700 font-medium">Total Saleable Area</p>
              <p className="font-mono font-bold text-sm text-blue-700 truncate" data-testid="exec-saleable-area">{formatNumber(plData.finalSaleAreaSqft, 0)} sft</p>
            </div>
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-sm text-center">
              <p className="text-[10px] uppercase tracking-wider text-purple-700 font-medium">{plData.isGreenfield ? 'JV Ratio (L : D)' : 'Sharing Ratio (S : D)'}</p>
              <p className="font-mono font-bold text-sm text-purple-700 truncate" data-testid="exec-sharing-ratio">{formatNumber(plData.societySharePercent, 0)}% : {formatNumber(plData.developerSharePercent, 0)}%</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Section A: Revenue */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-chart-1 border-b pb-1">A. REVENUE</h4>
            {plData.isGreenfield && plData.totalSaleRevenue !== plData.totalRevenue ? (
              <div className="space-y-1">
                <div className="flex justify-between items-center p-2 bg-chart-1/10 rounded-sm min-w-0">
                  <span className="text-sm shrink-0">Gross Revenue (from Sales)</span>
                  <span className="font-mono font-semibold text-sm text-chart-1 truncate ml-2">{formatCrores(plData.totalSaleRevenue)}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-amber-50 rounded-sm min-w-0">
                  <span className="text-sm text-amber-700 shrink-0">Less: Landowner Share (JV)</span>
                  <span className="font-mono font-semibold text-sm text-amber-700 truncate ml-2">-{formatCrores(plData.totalSaleRevenue - plData.totalRevenue)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-chart-1/20 rounded-sm min-w-0">
                  <span className="font-medium shrink-0">Net Developer Revenue</span>
                  <span className="font-mono font-bold text-base text-chart-1 truncate ml-2">{formatCrores(plData.totalRevenue)}</span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center p-3 bg-chart-1/10 rounded-sm min-w-0">
                <span className="font-medium shrink-0">Total Revenue (from Sales)</span>
                <span className="font-mono font-bold text-base text-chart-1 truncate ml-2">{formatCrores(plData.totalRevenue)}</span>
              </div>
            )}
          </div>

          {/* Section B: Cost Breakdown */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground border-b pb-1">B. COSTS</h4>
            <div className="grid gap-2">
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">1. FSI & Sanction Costs</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.fsiTotalCosts)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">{plData.isGreenfield ? '2. JV / Project Costs' : '2. Society Costs (Rent, Shifting, etc.)'}</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.totalSocietyExpenses)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">3. Construction Cost</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.totalConstructionCost)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">4. Other Costs (Marketing, Misc, Brokerage)</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.totalOtherCosts)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">5. DA Charges (Stamp Duty, Registration)</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.totalDACharges)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-sm min-w-0">
                <span className="shrink-0">6. Bank Interest on Project</span>
                <span className="font-mono font-semibold text-sm truncate ml-2">{formatCrores(plData.totalBankInterest)}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-sm min-w-0">
                <span className="font-semibold shrink-0">TOTAL COSTS</span>
                <span className="font-mono font-bold text-base truncate ml-2">{formatCrores(plData.totalExpenses)}</span>
              </div>
              {/* Basic FSI what-if footnote — shown only when Paid FSI / TDR are present */}
              {plData.fsiTotalCostsBasicMode != null && plData.fsiTotalCosts > plData.fsiTotalCostsBasicMode && (
                <div className="flex justify-between items-center p-2 bg-blue-50/60 border border-blue-200 rounded-sm text-xs" data-testid="pl-basic-fsi-note">
                  <span className="text-blue-800">
                    <span className="font-semibold">Alt. scenario:</span> If Basic FSI 1.1 only (no Paid FSI + TDR), total costs would drop by
                  </span>
                  <span className="font-mono font-semibold text-blue-800 shrink-0 ml-2">
                    {formatCrores(plData.fsiTotalCosts - plData.fsiTotalCostsBasicMode)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Section C: Initial Investment */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-amber-600 border-b pb-1">C. INITIAL INVESTMENT (At Start)</h4>
            <div className="grid gap-1 text-sm">
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">TDR Cost</span>
                <span className="font-mono">{formatCrores(plData.tdrCost)}</span>
              </div>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">Paid FSI & Ancillary (20% min ₹50L)</span>
                <span className="font-mono">{formatCrores(plData.minPaidFSIInvestment)}</span>
              </div>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">Sanction Costs (excl. TDR/Paid FSI)</span>
                <span className="font-mono">{formatCrores(plData.sanctionCostsExTDRPaid)}</span>
              </div>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">DA Charges</span>
                <span className="font-mono">{formatCrores(plData.totalDACharges)}</span>
              </div>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">Shifting Charges (50%)</span>
                <span className="font-mono">{formatCrores(plData.shiftingInInitial)}</span>
              </div>
              {plData.bettermentOnVacation > 0 && (
                <div className="flex justify-between items-center p-2">
                  <span className="text-muted-foreground">Betterment (50% on vacation)</span>
                  <span className="font-mono">{formatCrores(plData.bettermentOnVacation)}</span>
                </div>
              )}
              {plData.depositCost > 0 && (
                <div className="flex justify-between items-center p-2">
                  <span className="text-muted-foreground">Deposit ({formData.depositMonths || 0} months)</span>
                  <span className="font-mono">{formatCrores(plData.depositCost)}</span>
                </div>
              )}
              {plData.brokerageCostSociety > 0 && (
                <div className="flex justify-between items-center p-2">
                  <span className="text-muted-foreground">Brokerage</span>
                  <span className="font-mono">{formatCrores(plData.brokerageCostSociety)}</span>
                </div>
              )}
              {plData.ulcCharges > 0 && (
                <div className="flex justify-between items-center p-2">
                  <span className="text-muted-foreground">ULC Charges</span>
                  <span className="font-mono">{formatCrores(plData.ulcCharges)}</span>
                </div>
              )}
              {plData.bgBaseAmount > 0 && (
                <div className="flex justify-between items-center p-2 bg-amber-100/50 rounded-sm">
                  <span className="text-amber-700 font-medium">Bank Guarantee (Full Amount)</span>
                  <span className="font-mono text-amber-700">{formatCrores(plData.bgBaseAmount)}</span>
                </div>
              )}
              {plData.jvSecurityDepositRefundable > 0 && (
                <div className="flex justify-between items-center p-2 bg-green-100/50 rounded-sm">
                  <span className="text-green-700 font-medium">Security Deposit (Refundable)</span>
                  <span className="font-mono text-green-700">{formatCrores(plData.jvSecurityDepositRefundable)}</span>
                </div>
              )}
              {plData.jvSecurityDepositNonRefundable > 0 && (
                <div className="flex justify-between items-center p-2 bg-red-100/50 rounded-sm">
                  <span className="text-red-700 font-medium">Security Deposit (Non-Refundable)</span>
                  <span className="font-mono text-red-700">{formatCrores(plData.jvSecurityDepositNonRefundable)}</span>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center p-3 bg-amber-500/10 rounded-sm border border-amber-500/30 min-w-0">
              <span className="font-medium shrink-0">Minimum Investment Required</span>
              <span className="font-mono font-bold text-base text-amber-600 truncate ml-2">{formatCrores(plData.initialInvestment)}</span>
            </div>
            {plData.initialInvestmentBasicMode != null && (
              <div
                className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-sm border border-emerald-500/30 min-w-0 -mt-2"
                data-testid="pl-initial-investment-basic"
              >
                <span className="font-medium shrink-0 text-emerald-800">
                  Alternate — Basic FSI 1.1 Sanction Only
                </span>
                <span className="font-mono font-bold text-base text-emerald-700 truncate ml-2">
                  {formatCrores(plData.initialInvestmentBasicMode)}
                  {(plData.initialInvestment - plData.initialInvestmentBasicMode) > 0 && (
                    <span className="text-xs text-emerald-700 font-normal ml-2">
                      (saves {formatCrores(plData.initialInvestment - plData.initialInvestmentBasicMode)})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Section D: Project Metrics */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground border-b pb-1">D. PROJECT METRICS</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Constructed Area</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-constructed-area">{formatNumber(plData.totalConstructedAreaSqft, 0)} sft</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Saleable Area</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-saleable-area">{formatNumber(plData.finalSaleAreaSqft, 0)} sft</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{plData.isGreenfield ? 'JV Ratio (L : D)' : 'Sharing Ratio (S : D)'}</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-sharing-ratio">
                  {formatNumber(plData.societySharePercent, 0)}% : {formatNumber(plData.developerSharePercent, 0)}%
                </p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. Flats for Sale</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-flats">{plData.numFlatsForSale} units</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Building Height</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-building-height">{plData.proposedBuildingHeight} m</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Project Duration</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-duration">{formatNumber(plData.durationYears * 12, 0)} months</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Construction Cost</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-construction-cost">{formatCrores(plData.totalConstructionCost)}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Sale Rate</p>
                <p className="font-mono font-semibold text-sm truncate" data-testid="metric-sale-rate">₹{formatNumber(plData.saleRatePerSqft, 0)}/sft</p>
              </div>
            </div>
          </div>

          {/* Section E: Profitability */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-primary border-b pb-1">E. PROFITABILITY ANALYSIS</h4>
            <div className="grid gap-2">
              <div className={`flex justify-between items-center p-4 rounded-sm ${plData.netProfit >= 0 ? 'bg-chart-1/20' : 'bg-destructive/20'}`}>
                <span className="font-bold text-lg">NET PROFIT</span>
                <span className={`font-mono font-bold text-2xl ${plData.netProfit >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                  {formatCrores(plData.netProfit)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground px-2">
                Bank Interest (Part B) on project costs is already included in Total Expenses
              </p>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">Profit Margin</span>
                <span className={`font-mono font-semibold ${plData.profitMarginPercent >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                  {formatNumber(plData.profitMarginPercent)}%
                </span>
              </div>
              <div className="flex justify-between items-center p-2">
                <span className="text-muted-foreground">Return on Investment (ROI)</span>
                <span className={`font-mono font-semibold ${plData.returnOnInvestment >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                  {formatNumber(plData.returnOnInvestment)}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============ DUAL JV P&L COMPARISON (Greenfield only) ============ */}
      {plData.dualJV && (
        <Card className="rounded-sm shadow-sm border-purple-300" data-testid="dual-jv-comparison">
          <CardHeader className="bg-purple-50 border-b border-purple-200">
            <CardTitle className="font-heading tracking-tight text-purple-700">JV COMPARISON: Area Sharing vs Revenue Sharing</CardTitle>
            <CardDescription>Side-by-side P&L for both JV structures at {plData.dualJV.jvRatioFlats}% landowner share</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Area Sharing Column */}
              {(() => {
                const as = plData.dualJV.areaSharing;
                const isSelected = plData.dualJV.selectedType === "area_sharing";
                return (
                  <div className={`p-4 rounded border-2 space-y-3 ${isSelected ? 'border-blue-500 bg-blue-50/30' : 'border-muted'}`} data-testid="jv-area-sharing-col">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm">Area Sharing</span>
                      {isSelected && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">SELECTED</span>}
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground">Gross Revenue</span><span className="font-mono">{formatCrores(as.totalSaleRevenue)}</span></div>
                      <div className="flex justify-between text-amber-700"><span>Less: Landowner Area Value</span><span className="font-mono">-{formatCrores(as.landownerShare)}</span></div>
                      <div className="flex justify-between font-medium border-t pt-1"><span>Developer Revenue</span><span className="font-mono text-chart-1">{formatCrores(as.totalRevenue)}</span></div>
                      <Separator className="my-1" />
                      <div className="flex justify-between"><span className="text-muted-foreground">Developer Sale Area</span><span className="font-mono">{Math.round(as.developerSaleArea).toLocaleString('en-IN')} sft</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Landowner Area</span><span className="font-mono">{Math.round(as.jvPartnerAreaSqft).toLocaleString('en-IN')} sft</span></div>
                      <Separator className="my-1" />
                      <div className="flex justify-between"><span className="text-muted-foreground">FSI & Sanction</span><span className="font-mono">{formatCrores(as.fsiTotalCosts)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{plData.isGreenfield ? 'DA Charges' : 'Society + DA'}</span><span className="font-mono">{formatCrores(as.totalSocietyExpenses + as.totalDACharges)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Construction</span><span className="font-mono">{formatCrores(as.totalConstructionCost)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Marketing (Dev area)</span><span className="font-mono">{formatCrores(as.marketingCost)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Bank Interest</span><span className="font-mono">{formatCrores(as.totalBankInterest)}</span></div>
                      <div className="flex justify-between text-red-600"><span>GST on Partner Area</span><span className="font-mono">{formatCrores(as.gstOnPartnerArea)}</span></div>
                      {as.jvSecurityDepositNonRefundable > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Non-Refund Deposit</span><span className="font-mono">{formatCrores(as.jvSecurityDepositNonRefundable)}</span></div>}
                      {as.jvRefundableDepositInterest > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Deposit Interest (6%)</span><span className="font-mono">{formatCrores(as.jvRefundableDepositInterest)}</span></div>}
                      <div className="flex justify-between font-medium border-t pt-1"><span>Total Expenses</span><span className="font-mono">{formatCrores(as.totalExpenses)}</span></div>
                      <Separator className="my-1" />
                      <div className={`flex justify-between font-semibold text-sm p-2 rounded ${as.grossProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span>Net Profit</span>
                        <span className={`font-mono ${as.grossProfit >= 0 ? 'text-green-700' : 'text-destructive'}`}>{formatCrores(as.grossProfit)}</span>
                      </div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-mono">{as.profitMarginPercent.toFixed(1)}%</span></div>
                    </div>
                  </div>
                );
              })()}

              {/* Revenue Sharing Column */}
              {(() => {
                const rs = plData.dualJV.revenueSharing;
                const isSelected = plData.dualJV.selectedType === "revenue_sharing";
                return (
                  <div className={`p-4 rounded border-2 space-y-3 ${isSelected ? 'border-blue-500 bg-blue-50/30' : 'border-muted'}`} data-testid="jv-revenue-sharing-col">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm">Revenue Sharing</span>
                      {isSelected && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">SELECTED</span>}
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground">Gross Revenue</span><span className="font-mono">{formatCrores(rs.totalSaleRevenue)}</span></div>
                      <div className="flex justify-between text-amber-700"><span>Less: Landowner Revenue Share</span><span className="font-mono">-{formatCrores(rs.landownerShare)}</span></div>
                      <div className="flex justify-between font-medium border-t pt-1"><span>Developer Revenue</span><span className="font-mono text-chart-1">{formatCrores(rs.totalRevenue)}</span></div>
                      <Separator className="my-1" />
                      <div className="flex justify-between"><span className="text-muted-foreground">Developer Sale Area</span><span className="font-mono">{Math.round(rs.developerSaleArea).toLocaleString('en-IN')} sft</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Landowner Revenue</span><span className="font-mono">{formatCrores(rs.jvPartnerRevenue)}</span></div>
                      <Separator className="my-1" />
                      <div className="flex justify-between"><span className="text-muted-foreground">FSI & Sanction</span><span className="font-mono">{formatCrores(rs.fsiTotalCosts)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{plData.isGreenfield ? 'DA Charges' : 'Society + DA'}</span><span className="font-mono">{formatCrores(rs.totalSocietyExpenses + rs.totalDACharges)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Construction</span><span className="font-mono">{formatCrores(rs.totalConstructionCost)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Marketing (Full area)</span><span className="font-mono">{formatCrores(rs.marketingCost)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Bank Interest</span><span className="font-mono">{formatCrores(rs.totalBankInterest)}</span></div>
                      <div className="flex justify-between text-green-600"><span>GST</span><span className="font-mono">₹0 (N/A)</span></div>
                      {rs.jvSecurityDepositNonRefundable > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Non-Refund Deposit</span><span className="font-mono">{formatCrores(rs.jvSecurityDepositNonRefundable)}</span></div>}
                      {rs.jvRefundableDepositInterest > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Deposit Interest (6%)</span><span className="font-mono">{formatCrores(rs.jvRefundableDepositInterest)}</span></div>}
                      <div className="flex justify-between font-medium border-t pt-1"><span>Total Expenses</span><span className="font-mono">{formatCrores(rs.totalExpenses)}</span></div>
                      <Separator className="my-1" />
                      <div className={`flex justify-between font-semibold text-sm p-2 rounded ${rs.grossProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span>Net Profit</span>
                        <span className={`font-mono ${rs.grossProfit >= 0 ? 'text-green-700' : 'text-destructive'}`}>{formatCrores(rs.grossProfit)}</span>
                      </div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-mono">{rs.profitMarginPercent.toFixed(1)}%</span></div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Recommendation */}
            {(() => {
              const as = plData.dualJV.areaSharing;
              const rs = plData.dualJV.revenueSharing;
              const areaBetter = as.grossProfit > rs.grossProfit;
              const diff = Math.abs(as.grossProfit - rs.grossProfit);
              return (
                <div className={`mt-4 p-3 rounded text-xs ${areaBetter ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'}`}>
                  <span className="font-semibold">{areaBetter ? 'Area Sharing' : 'Revenue Sharing'}</span> yields {formatCrores(diff)} higher profit.
                  {plData.dualJV.selectedType !== (areaBetter ? 'area_sharing' : 'revenue_sharing') && (
                    <span className="text-amber-700 ml-1">Consider switching to the more profitable structure.</span>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ============ DETAILED P&L BREAKDOWN ============ */}
      
      {/* Revenue Section */}
      <Card className="rounded-sm shadow-sm" data-testid="pl-revenue-section">
        <CardHeader className="bg-chart-1/10 border-b">
          <CardTitle className="font-heading tracking-tight text-chart-1">A. REVENUE (Detailed)</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {/* Revenue Breakdown by Property Type */}
            {(plData.revenueFlats > 0 || plData.revenueOffices > 0 || plData.revenueShops > 0) ? (
              <>
                {plData.revenueFlats > 0 && (
                  <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
                    <div>
                      <p className="font-medium">Revenue from Flats</p>
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(plData.saleableAreaFlats, 0)} sft (@ {plData.loadingPctFlats || 40}% loading) × ₹{formatNumber(plData.saleRateFlats, 0)}/sft
                      </p>
                    </div>
                    <span className="font-mono font-semibold">{formatCurrency(plData.revenueFlats)}</span>
                  </div>
                )}
                {plData.revenueOffices > 0 && (
                  <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
                    <div>
                      <p className="font-medium">Revenue from Offices</p>
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(plData.saleableAreaOffices, 0)} sft (@ {plData.loadingPctOffices || 45}% loading) × ₹{formatNumber(plData.saleRateOffices, 0)}/sft
                      </p>
                    </div>
                    <span className="font-mono font-semibold">{formatCurrency(plData.revenueOffices)}</span>
                  </div>
                )}
                {plData.revenueShops > 0 && (
                  <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
                    <div>
                      <p className="font-medium">Revenue from Shops</p>
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(plData.saleableAreaShops, 0)} sft (@ {plData.loadingPctShops || 50}% loading) × ₹{formatNumber(plData.saleRateShops, 0)}/sft
                      </p>
                    </div>
                    <span className="font-mono font-semibold">{formatCurrency(plData.revenueShops)}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
                <div>
                  <p className="font-medium">Sale of Constructed Area</p>
                  <p className="text-xs text-muted-foreground">
                    {formatNumber(plData.finalSaleAreaSqft, 0)} sft × ₹{formatNumber(plData.saleRatePerSqft, 0)}/sft
                  </p>
                </div>
                <span className="font-mono font-semibold text-lg">{formatCurrency(plData.totalSaleRevenue)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between items-center p-3 bg-chart-1/10 rounded-sm">
              <span className="font-semibold text-lg">Gross Sale Revenue</span>
              <span className="font-mono font-bold text-xl text-chart-1">{formatCurrency(plData.totalSaleRevenue)}</span>
            </div>
            {plData.isGreenfield && plData.totalSaleRevenue !== plData.totalRevenue && (
              <>
                <div className="flex justify-between items-center p-3 bg-amber-50 rounded-sm">
                  <span className="text-amber-700">Less: Landowner Share ({plData.jvType === "revenue_sharing" ? "Revenue" : "Area"} Sharing)</span>
                  <span className="font-mono font-semibold text-amber-700">-{formatCurrency(plData.totalSaleRevenue - plData.totalRevenue)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-chart-1/20 rounded-sm">
                  <span className="font-semibold text-lg">Net Developer Revenue</span>
                  <span className="font-mono font-bold text-xl text-chart-1">{formatCurrency(plData.totalRevenue)}</span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Landowner Share Section (JV Greenfield only) */}
      {plData.isGreenfield && (plData.jvPartnerArea > 0 || plData.jvPartnerRevenue > 0) && (
        <Card className="rounded-sm shadow-sm border-purple-300" data-testid="pl-landowner-share-section">
          <CardHeader className="bg-purple-50 border-b border-purple-200">
            <CardTitle className="font-heading tracking-tight text-purple-700">LANDOWNER'S SHARE (JV)</CardTitle>
            <CardDescription>
              {plData.jvType === "revenue_sharing" ? "Revenue Sharing based on Sale Rates" : "Area Sharing at Market Value"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2 text-sm">
              {/* Header Row */}
              <div className="grid grid-cols-4 gap-2 font-medium text-muted-foreground border-b pb-2 text-xs uppercase">
                <span>Property Type</span>
                <span className="text-right">{plData.jvType === "revenue_sharing" ? "Share %" : "Area (sqft)"}</span>
                <span className="text-right">Rate/sqft</span>
                <span className="text-right">Value</span>
              </div>
              
              {/* Flats Row */}
              {(plData.jvPartnerAreaFlats > 0 || plData.jvPartnerRevenueFlats > 0) && (
                <div className="grid grid-cols-4 gap-2 p-2 bg-green-50 rounded">
                  <span className="font-medium text-green-700">Flats</span>
                  <span className="text-right font-mono">
                    {plData.jvType === "revenue_sharing" 
                      ? `${plData.jvRatioFlats}%` 
                      : formatNumber(plData.jvPartnerAreaFlats / 0.092903, 0)}
                  </span>
                  <span className="text-right font-mono">
                    ₹{formatNumber(parseFloat(formData.saleRateFlats) || 0, 0)}
                  </span>
                  <span className="text-right font-mono font-semibold text-green-700">
                    {formatCrores(plData.jvType === "revenue_sharing" ? plData.jvPartnerRevenueFlats : plData.jvPartnerAreaFlats / 0.092903 * (parseFloat(formData.saleRateFlats) || 0))}
                  </span>
                </div>
              )}
              
              {/* Offices Row */}
              {(plData.jvPartnerAreaOffices > 0 || plData.jvPartnerRevenueOffices > 0) && (
                <div className="grid grid-cols-4 gap-2 p-2 bg-blue-50 rounded">
                  <span className="font-medium text-blue-700">Offices</span>
                  <span className="text-right font-mono">
                    {plData.jvType === "revenue_sharing" 
                      ? `${plData.jvRatioOffices}%` 
                      : formatNumber(plData.jvPartnerAreaOffices / 0.092903, 0)}
                  </span>
                  <span className="text-right font-mono">
                    ₹{formatNumber(parseFloat(formData.saleRateOffices) || parseFloat(formData.saleRateFlats) || 0, 0)}
                  </span>
                  <span className="text-right font-mono font-semibold text-blue-700">
                    {formatCrores(plData.jvType === "revenue_sharing" ? plData.jvPartnerRevenueOffices : plData.jvPartnerAreaOffices / 0.092903 * (parseFloat(formData.saleRateOffices) || parseFloat(formData.saleRateFlats) || 0))}
                  </span>
                </div>
              )}
              
              {/* Shops Row */}
              {(plData.jvPartnerAreaShops > 0 || plData.jvPartnerRevenueShops > 0) && (
                <div className="grid grid-cols-4 gap-2 p-2 bg-amber-50 rounded">
                  <span className="font-medium text-amber-700">Shops</span>
                  <span className="text-right font-mono">
                    {plData.jvType === "revenue_sharing" 
                      ? `${plData.jvRatioShops}%` 
                      : formatNumber(plData.jvPartnerAreaShops / 0.092903, 0)}
                  </span>
                  <span className="text-right font-mono">
                    ₹{formatNumber(parseFloat(formData.saleRateShops) || parseFloat(formData.saleRateFlats) || 0, 0)}
                  </span>
                  <span className="text-right font-mono font-semibold text-amber-700">
                    {formatCrores(plData.jvType === "revenue_sharing" ? plData.jvPartnerRevenueShops : plData.jvPartnerAreaShops / 0.092903 * (parseFloat(formData.saleRateShops) || parseFloat(formData.saleRateFlats) || 0))}
                  </span>
                </div>
              )}
              
              {/* Total Row */}
              <Separator className="my-2" />
              <div className="grid grid-cols-4 gap-2 p-3 bg-purple-100 rounded font-semibold">
                <span className="text-purple-700">TOTAL LANDOWNER VALUE</span>
                <span className="text-right font-mono">
                  {plData.jvType !== "revenue_sharing" && formatNumber(plData.jvPartnerAreaSqft, 0)}
                </span>
                <span className="text-right">-</span>
                <span className="text-right font-mono text-lg text-purple-700">
                  {formatCrores(plData.jvType === "revenue_sharing" 
                    ? plData.jvPartnerRevenue 
                    : plData.jvPartnerAreaSqft * (parseFloat(formData.saleRateFlats) || 0))}
                </span>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded space-y-1">
              <p><strong>JV Type:</strong> {plData.jvType === "revenue_sharing" ? "Revenue Sharing (Sale Rate based)" : "Area Sharing (Market Value)"}</p>
              <p><strong>Share %:</strong> Flats: {plData.jvRatioFlats}% | Offices: {plData.jvRatioOffices}% | Shops: {plData.jvRatioShops}%</p>
              {plData.jvGSTOnPartnerArea > 0 && (
                <p><strong>GST on Partner's Area:</strong> {formatCrores(plData.jvGSTOnPartnerArea)} (included in JV Costs)</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses Section */}
      <Card className="rounded-sm shadow-sm" data-testid="pl-expenses-section">
        <CardHeader className="bg-secondary/50 border-b">
          <CardTitle className="font-heading tracking-tight">B. EXPENSES</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* B1. FSI & Sanction Costs */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B1. FSI & Sanction Costs</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Amenity TDR</span><span className="font-mono">{formatCurrency(plData.amenityTDRCost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Slum TDR</span><span className="font-mono">{formatCurrency(plData.slumTDRCost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Paid FSI</span><span className="font-mono">{formatCurrency(plData.paidFSICost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ancillary FSI</span><span className="font-mono">{formatCurrency(plData.ancillaryFSICost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Land Dev Charges</span><span className="font-mono">{formatCurrency(plData.landDevCharges)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Building Dev Charges</span><span className="font-mono">{formatCurrency(plData.buildingDevCharges)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Upkar</span><span className="font-mono">{formatCurrency(plData.upkar)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fire Fighting</span><span className="font-mono">{formatCurrency(plData.fireFightingCharges)}</span></div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold"><span>Subtotal FSI</span><span className="font-mono">{formatCurrency(plData.fsiTotalCosts)}</span></div>
            </div>
          </div>

          {/* B2. DA Charges */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B2. DA Charges</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Stamp Duty</span><span className="font-mono">{formatCurrency(plData.totalStampDuty)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Registration</span><span className="font-mono">{formatCurrency(plData.totalRegistration)}</span></div>
              {plData.scrutinyCharges > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Scrutiny</span><span className="font-mono">{formatCurrency(plData.scrutinyCharges)}</span></div>}
              {plData.lbtCharges > 0 && <div className="flex justify-between"><span className="text-muted-foreground">LBT</span><span className="font-mono">{formatCurrency(plData.lbtCharges)}</span></div>}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold"><span>Subtotal DA</span><span className="font-mono">{formatCurrency(plData.totalDACharges)}</span></div>
            </div>
          </div>

          {/* B3. Society / JV Expenses */}
          {!plData.isGreenfield && (
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B3. Society Expenses</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Shifting</span><span className="font-mono">{formatCurrency(plData.shiftingCharges)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Corpus</span><span className="font-mono">{formatCurrency(plData.corpusCharges)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Betterment</span><span className="font-mono">{formatCurrency(plData.bettermentCharges)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Rental</span><span className="font-mono">{formatCurrency(plData.totalRentalCost)}</span></div>
              {plData.brokerageCostSociety > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Brokerage</span><span className="font-mono">{formatCurrency(plData.brokerageCostSociety)}</span></div>}
              {plData.pmcCost > 0 && <div className="flex justify-between"><span className="text-muted-foreground">SNA</span><span className="font-mono">{formatCurrency(plData.pmcCost)}</span></div>}
              {plData.bgCost > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Bank Guarantee Cost <span className="text-xs">(₹{(plData.bgBaseAmount/100000).toFixed(1)}L × {plData.durationYears}yr × {plData.bgRatePA}%)</span></span><span className="font-mono">{formatCurrency(plData.bgCost)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="font-mono">{formatCurrency(plData.totalGST)}</span></div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold"><span>Subtotal Society</span><span className="font-mono">{formatCurrency(plData.totalSocietyExpenses)}</span></div>
            </div>
          </div>
          )}

          {/* B4. Construction Cost */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B4. Construction Cost</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Building Construction ({formatNumber(plData.totalConstructedAreaSqft, 0)} sft × ₹{formatNumber(plData.constructionCostPerSqft, 0)}/sft)</span>
                <span className="font-mono">{formatCurrency(plData.baseConstructionCost)}</span>
              </div>
              {plData.recreationalFloorCost > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Recreational Floor ({formatNumber(parseFloat(formData.recreationalFloorAreaSqft) || 0, 0)} sft)</span>
                  <span className="font-mono">{formatCurrency(plData.recreationalFloorCost)}</span>
                </div>
              )}
              {plData.basementCost > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Basements ({formData.numBasements} × {formatNumber(parseFloat(formData.basementAreaPerFloorSqft) || 0, 0)} sft × ₹{formatNumber(parseFloat(formData.basementCostPerSqft) || 0, 0)}/sft)</span>
                  <span className="font-mono">{formatCurrency(plData.basementCost)}</span>
                </div>
              )}
              {plData.additionalParkingFloorCost > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Addl Parking Floors ({formData.numAdditionalParkingFloors} × {formatNumber(parseFloat(formData.additionalParkingFloorAreaSqft) || 0, 0)} sft × ₹{formatNumber(parseFloat(formData.additionalParkingCostPerSqft) || 0, 0)}/sft)</span>
                  <span className="font-mono">{formatCurrency(plData.additionalParkingFloorCost)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Total Construction</span>
                <span className="font-mono">{formatCurrency(plData.totalConstructionCost)}</span>
              </div>
            </div>
          </div>

          {/* B5. Other Costs */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B5. Other Costs</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Marketing</span><span className="font-mono">{formatCurrency(plData.totalMarketingCost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Miscellaneous</span><span className="font-mono">{formatCurrency(plData.totalMiscExp)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Brokerage</span><span className="font-mono">{formatCurrency(plData.totalBrokerageCommission)}</span></div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold"><span>Subtotal Other</span><span className="font-mono">{formatCurrency(plData.totalOtherCosts)}</span></div>
            </div>
          </div>

          {/* B6. Bank Interest */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B6. Bank Interest</h4>
            <div className="space-y-2 text-sm bg-secondary/20 p-3 rounded-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Interest Part A (80% @ 8%)</span><span className="font-mono">{formatCurrency(plData.interestPartA)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Interest Part B (@ {formData.projectInterestRate !== undefined && formData.projectInterestRate !== "" ? formData.projectInterestRate : 12}%)</span><span className="font-mono">{formatCurrency(plData.interestPartB)}</span></div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold"><span>Subtotal Interest</span><span className="font-mono">{formatCurrency(plData.totalBankInterest)}</span></div>
            </div>
          </div>

          {/* B7. JV Costs (Greenfield only) */}
          {plData.isGreenfield && (plData.jvSecurityDepositNonRefundable > 0 || plData.jvGSTOnPartnerArea > 0 || plData.jvRefundableDepositInterest > 0 || plData.bankGuaranteeCost > 0) && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">B7. JV Costs</h4>
              <div className="space-y-2 text-sm bg-blue-50 p-3 rounded-sm border border-blue-200">
                {plData.jvSecurityDepositNonRefundable > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Non-Refundable Deposit (added to Landowner's share)</span>
                    <span className="font-mono">{formatCurrency(plData.jvSecurityDepositNonRefundable)}</span>
                  </div>
                )}
                {plData.jvRefundableDepositInterest > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Refundable Deposit Interest ({formatCurrency(plData.jvSecurityDepositRefundable)} × 6% × {formatNumber(plData.durationYears, 1)} yr)</span>
                    <span className="font-mono">{formatCurrency(plData.jvRefundableDepositInterest)}</span>
                  </div>
                )}
                {plData.bankGuaranteeCost > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Bank Guarantee Cost
                        <span className="text-xs ml-1">
                          (₹{(plData.bgBaseAmount / 10000000).toFixed(2)} Cr × {plData.bgRatePA}%{plData.bgStagedRelease ? ' staged' : ' flat'})
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(plData.bankGuaranteeCost)}</span>
                    </div>
                    {plData.bgStagedRelease && plData.bgStages?.length > 0 && (
                      <div className="pl-3 space-y-1 text-xs text-muted-foreground border-l-2 border-blue-200 ml-1">
                        {plData.bgStages.map((s) => (
                          <div key={s.stage} className="flex justify-between">
                            <span>Stage {s.stage}: ₹{(s.outstanding / 10000000).toFixed(2)} Cr × {s.months} mo</span>
                            <span className="font-mono">₹{(s.cost / 100000).toFixed(1)} L</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {plData.jvGSTOnPartnerArea > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST on Partner's Area</span>
                    <span className="font-mono">{formatCurrency(plData.jvGSTOnPartnerArea)}</span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Subtotal JV Costs</span>
                  <span className="font-mono">{formatCurrency((plData.jvSecurityDepositNonRefundable || 0) + (plData.jvRefundableDepositInterest || 0) + (plData.jvGSTOnPartnerArea || 0) + (plData.bankGuaranteeCost || 0))}</span>
                </div>
              </div>
            </div>
          )}

          <Separator className="my-4" />
          <div className="flex justify-between items-center p-4 bg-secondary rounded-sm">
            <span className="font-semibold text-lg">Total Expenses</span>
            <span className="font-mono font-bold text-xl">{formatCurrency(plData.totalExpenses)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Final Profit/Loss */}
      <Card className={`rounded-sm shadow-sm border-2 ${isProfitable ? 'border-chart-1' : 'border-destructive'}`} data-testid="pl-final-section">
        <CardHeader className={isProfitable ? 'bg-chart-1/10' : 'bg-destructive/10'}>
          <CardTitle className="font-heading tracking-tight">C. PROFIT / LOSS</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
            <span className="text-muted-foreground">Total Revenue (A)</span>
            <span className="font-mono font-semibold text-chart-1">{formatCurrency(plData.totalRevenue)}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-sm">
            <span className="text-muted-foreground">Total Expenses (B)</span>
            <span className="font-mono font-semibold">{formatCurrency(plData.totalExpenses)}</span>
          </div>
          <Separator />
          <div className={`flex justify-between items-center p-4 rounded-sm ${isProfitable ? 'bg-chart-1/20' : 'bg-destructive/20'}`}>
            <div>
              <span className="font-semibold text-xl">Net Profit / Loss</span>
              <p className="text-xs text-muted-foreground mt-1">ROI: {formatNumber(plData.returnOnInvestment)}%</p>
            </div>
            <span className={`font-mono font-bold text-3xl ${isProfitable ? 'text-chart-1' : 'text-destructive'}`}>
              {formatCurrency(plData.netProfit)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PLSheet;
